import clsx from 'clsx';
import PropTypes from 'prop-types';

import { PLACEHOLDER_IMAGE } from 'constants/shop';
import BundleCount from 'honeydew/BundleCount';
import PointsTag from 'shared/PointsTag';
import { getPercentOffOriginalPrice, getProductOriginalPrice, getShowOriginalPrice } from 'utils/productUtils';
import twclsx from 'utils/twclsx';

import { PRODUCT_DATA_PROP_TYPE } from './types';

/**
 * Presentational component for displaying a product card
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child elements to render in the card
 * @param {boolean} [props.displayColdPacked=false] - Whether to show cold pack indicator
 * @param {boolean} [props.displayFrozen=false] - Whether to show frozen indicator
 * @param {boolean} [props.isGwp=false] - Whether the product is a gift with purchase item
 * @param {'horizontal'|'vertical'} [props.layout='vertical'] - Layout orientation of the card
 * @param {boolean} [props.lazyLoadImage=true] - Whether to lazy load the product image
 * @param {Function} [props.navigateToPDP] - Handler for navigating to product detail page
 * @param {string} [props.plusMembershipShortBranding] - Short branding text for plus membership (e.g. 'M+', 'IF+')
 * @param {import('./types').ProductData} props.productData - Product information to display
 * @param {boolean} props.showMembershipPreviewPrice - Whether to show preview price for members
 * @param {boolean} props.showMembershipPrice - Whether to show member pricing
 * @param {boolean} [props.isPerk=false] - Whether the product is a perk item
 * @param {boolean} [props.isPerkRedeemable=false] - Whether the perk item can be redeemed with points
 */
function ProductCardPresentation({
    children,
    displayColdPacked,
    displayFrozen,
    isGwp,
    isPerk,
    isPerkRedeemable,
    layout,
    lazyLoadImage = true,
    navigateToPDP,
    plusMembershipShortBranding,
    pointPrice,
    productData,
    showMembershipPreviewPrice,
    showMembershipPrice,
}) {
    const { brand, id, name, photoMed: imgSrc, soldOut, sourcingReason } = productData ?? {};

    return (
        <Wrapper
            layout={layout}
            navigateToPDP={navigateToPDP}
            data-product-id={id}
            aria-label={`View ${name} product details`}
        >
            <div className={clsx(layout === 'vertical' && 'mb-10')} data-testid="product-image-container">
                <div className="relative overflow-hidden rounded-xl">
                    {showMembershipPrice && (
                        <div
                            className="bg-gradient-berry-blend absolute top-0 left-0 z-10 h-5 w-full"
                            data-testid="membership-gradient"
                        />
                    )}
                    <ProductTags productData={productData} isGwp={isGwp} />
                    <div className={clsx('overflow-hidden', { 'size-[100px]': layout === 'horizontal' })}>
                        {productData.isBundle && (
                            <BundleCount
                                count={productData.portion}
                                className="absolute top-5 right-5"
                                width={layout === 'horizontal' ? 30 : 40}
                            />
                        )}
                        <img
                            src={imgSrc || PLACEHOLDER_IMAGE}
                            alt={name}
                            width={200}
                            height={200}
                            className={clsx('w-full rounded-xl object-cover', { 'opacity-50': soldOut })}
                            loading={lazyLoadImage ? 'lazy' : 'eager'}
                        />
                    </div>
                    <div className="absolute right-5 bottom-5 left-5 z-10 flex flex-row-reverse justify-between">
                        {children}
                    </div>
                </div>
            </div>
            <div className="flex flex-col">
                {sourcingReason && <SourcingReason>{sourcingReason}</SourcingReason>}
                <ProductTitle brand={brand} layout={layout} name={name} />
                {displayColdPacked && <UpperCaseTag>Cold pack</UpperCaseTag>}
                {displayFrozen && <UpperCaseTag>Frozen</UpperCaseTag>}
                <div>
                    {(() => {
                        if (isPerk) {
                            return <PointsTag locked={!isPerkRedeemable} pointPrice={pointPrice} />;
                        }

                        // Hide FREE price for gift with purchase variant
                        if (isGwp) {
                            return null;
                        }

                        if (productData.isGift) {
                            return (
                                <p className="font-grotesk-bold text-body-mobile-sm text-success md:text-body-mobile-md">
                                    FREE
                                </p>
                            );
                        }

                        if (showMembershipPrice) {
                            return (
                                <MembershipPricing
                                    plusMembershipShortBranding={plusMembershipShortBranding}
                                    productData={productData}
                                />
                            );
                        }

                        return (
                            <NonMembershipPricing
                                plusMembershipShortBranding={plusMembershipShortBranding}
                                productData={productData}
                                showMembershipPreviewPrice={showMembershipPreviewPrice}
                            />
                        );
                    })()}
                </div>
            </div>
        </Wrapper>
    );
}

ProductCardPresentation.propTypes = {
    children: PropTypes.node,
    displayColdPacked: PropTypes.bool,
    isGwp: PropTypes.bool,
    isPerk: PropTypes.bool,
    isPerkRedeemable: PropTypes.bool,
    layout: PropTypes.oneOf(['vertical', 'horizontal']),
    lazyLoadImage: PropTypes.bool,
    navigateToPDP: PropTypes.func,
    plusMembershipShortBranding: PropTypes.string,
    productData: PRODUCT_DATA_PROP_TYPE.isRequired,
    showMembershipPreviewPrice: PropTypes.bool.isRequired,
    showMembershipPrice: PropTypes.bool.isRequired,
};

ProductCardPresentation.defaultProps = {
    children: null,
    displayColdPacked: false,
    isGwp: false,
    isPerk: false,
    isPerkRedeemable: false,
    layout: 'vertical',
    lazyLoadImage: true,
    navigateToPDP: () => {},
    plusMembershipShortBranding: '',
};

export default ProductCardPresentation;

// PARTIALS...

/**
 * A wrapper component that provides layout and navigation functionality for product cards.
 * Handles both click and keyboard navigation events.
 *
 * @param {Object} props The component props
 * @param {React.ReactNode} props.children Child elements to render inside the wrapper
 * @param {string} [props.className] Additional CSS classes to apply
 * @param {'vertical'|'horizontal'} [props.layout='vertical'] Layout orientation of the wrapper
 * @param {Function} [props.navigateToPDP] Callback function to navigate to product detail page
 * @param {Object} [props.restProps] Additional HTML attributes spread to the wrapper div element
 * @returns {JSX.Element} The wrapped product card component
 */
function Wrapper({ children, className, layout, navigateToPDP, ...restProps }) {
    function handleClick() {
        navigateToPDP();
    }

    function handleKeyDown(e) {
        if (e.key === 'Enter' || e.code === 'Enter') {
            navigateToPDP();
        }

        return false;
    }

    return (
        <div
            className={twclsx(
                {
                    'flex gap-10': layout === 'horizontal',
                    'cursor-pointer': navigateToPDP,
                },
                className
            )}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="link"
            {...restProps}
        >
            {children}
        </div>
    );
}

Wrapper.propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    layout: PropTypes.oneOf(['vertical', 'horizontal']),
    navigateToPDP: PropTypes.func,
};

Wrapper.defaultProps = {
    className: '',
    layout: 'vertical',
    navigateToPDP: () => {},
};

/**
 * A tag component used to display product status/labels like "SOLD OUT", "NEW", etc.
 * Renders as an absolutely positioned div with rounded corners on top-left and bottom-right.
 *
 * @param {Object} props Component props
 * @param {React.ReactNode} props.children Content to display inside the tag
 * @param {string} [props.className] Additional CSS classes to apply to the tag
 * @returns {JSX.Element} The product tag component
 */
function ProductTag({ children, className }) {
    return (
        <div
            className={clsx(
                'absolute top-0 left-0 z-10 flex h-[22px] items-center rounded-tl-xl rounded-br-xl px-5 font-grotesk-bold text-body-mobile-xs text-black',
                className
            )}
        >
            {children}
        </div>
    );
}

ProductTag.propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
};

ProductTag.defaultProps = {
    className: '',
};

/**
 * A component that renders product status tags based on product data.
 * Only one tag will be shown at a time, with priority given in the following order:
 * SOLD OUT > FREEBIE > BUNDLE > SALE > SALE > NEW > LAST CHANCE > BEST SELLER
 *
 * @param {Object} props Component props
 * @param {boolean} [props.isGwp=false] - Whether this is a gift with purchase variant (hides FREEBIE tag)
 * @param {import('./types').ProductData} props.productData Product data object containing status flags
 * @returns {JSX.Element|null} A ProductTag component with appropriate styling, or null if no tag applies
 */
function ProductTags({ isGwp, productData }) {
    const { bestSeller, exitSku, isBundle, isGift, new: newProduct, sale, soldOut } = productData;

    if (soldOut) return <ProductTag className="bg-grey-light text-grey">SOLD OUT</ProductTag>;
    if (isGift && !isGwp) return <ProductTag className="bg-success text-white">FREEBIE</ProductTag>;
    if (isBundle) return <ProductTag className="bg-error text-white">BUNDLE</ProductTag>;
    if (sale) return <ProductTag className="bg-error text-white">SALE</ProductTag>;
    if (newProduct) return <ProductTag className="bg-[#CFE5BD] text-black">NEW</ProductTag>;
    if (exitSku) return <ProductTag className="bg-[#FAEAC0]">LAST CHANCE</ProductTag>;
    if (bestSeller) return <ProductTag className="bg-[#C8E2F3]">BEST SELLER</ProductTag>;

    return null;
}

ProductTags.propTypes = {
    isGwp: PropTypes.bool,
    productData: PRODUCT_DATA_PROP_TYPE.isRequired,
};

ProductTags.defaultProps = {
    isGwp: false,
};

/**
 * A component that displays the sourcing reason for a product in a styled container.
 * The sourcing reason appears as a small grey pill with light background.
 *
 * @param {Object} props Component props
 * @param {ReactNode} props.children The sourcing reason text to display
 * @returns {JSX.Element} A styled container with the sourcing reason text
 */
function SourcingReason({ children }) {
    return (
        <div className="mb-[2px] flex">
            <div className="shrink rounded-xl bg-off-white px-5 py-[2px] text-body-mobile-xs leading-[.9rem]! text-grey md:text-body-mobile-sm">
                {children}
            </div>
        </div>
    );
}

SourcingReason.propTypes = {
    children: PropTypes.node.isRequired,
};

/**
 * A component that displays a product's title, optionally including the brand name.
 * The title text is truncated based on the layout orientation.
 *
 * @param {Object} props Component props
 * @param {string} props.brand The product's brand name. Will be displayed in bold if provided.
 * @param {'vertical'|'horizontal'} props.layout The layout orientation of the card.
 *                                              Affects line clamping behavior.
 * @param {string} props.name The product's name/title
 * @returns {JSX.Element} A heading element containing the formatted product title
 */
function ProductTitle({ brand, layout, name }) {
    return (
        <h5
            className={clsx(
                'text-body-mobile-xs md:text-body-mobile-sm',
                layout === 'horizontal' ? 'line-clamp-2' : 'line-clamp-3'
            )}
        >
            {brand && <span className="font-grotesk-bold">{brand}</span>} {name}
        </h5>
    );
}

ProductTitle.propTypes = {
    brand: PropTypes.string,
    layout: PropTypes.oneOf(['vertical', 'horizontal']),
    name: PropTypes.string.isRequired,
};

ProductTitle.defaultProps = {
    brand: '',
    layout: 'vertical',
};

/**
 * Compact membership badge shown on product cards next to plus-member pricing.
 *
 * @param {Object} props Component props
 * @param {string} props.plusMembershipShortBranding Short branding text for plus membership (e.g. 'M+', 'IF+')
 * @returns {JSX.Element} A cranberry pill-shaped label
 */
function PlusMembershipChip({ plusMembershipShortBranding }) {
    return (
        <span className="text-body-xs inline-flex h-[19px] shrink-0 items-center justify-center rounded-md bg-cranberry px-5 font-grotesk-bold leading-none text-white">
            {plusMembershipShortBranding}
        </span>
    );
}

/**
 * Displays pricing information for non-members, including regular price, original price if applicable,
 * and optional membership preview pricing.
 *
 * @param {Object} props Component props
 * @param {string} props.plusMembershipShortBranding Short branding text for plus membership (e.g. 'M+', 'IF+')
 * @param {import('./types').ProductData} props.productData Product data containing pricing information
 * @param {boolean} props.showMembershipPreviewPrice Whether to show preview pricing for members
 */
function NonMembershipPricing({ plusMembershipShortBranding, productData, showMembershipPreviewPrice }) {
    return (
        <>
            <p className="flex flex-wrap items-baseline gap-5">
                <span
                    className={clsx('font-grotesk-bold text-body-mobile-sm md:text-body-mobile-md', {
                        'text-error': productData.sale || productData.isBundle,
                    })}
                >
                    {productData.isBundle && <>{productData.portion} for </>}${productData.price.toFixed(2)}
                </span>
                {getShowOriginalPrice(productData, 0, false) && (
                    <>
                        <span className="inline-block text-body-mobile-xs text-grey line-through md:text-body-mobile-sm">
                            ${getProductOriginalPrice(productData)?.toFixed(2)}
                        </span>
                        {!productData.isBundle && (
                            <span
                                className={clsx('text-body-mobile-xs md:text-body-mobile-sm', {
                                    'text-error': productData.sale,
                                })}
                            >
                                {getPercentOffOriginalPrice(productData, false)}%OFF
                            </span>
                        )}
                    </>
                )}
            </p>
            {showMembershipPreviewPrice && productData.plusMemberPrice > 0 && (
                <p className="flex flex-wrap items-center gap-x-5">
                    <span className="text-body-mobile-xs leading-none text-cranberry md:text-body-mobile-sm">
                        Pay ${productData.plusMemberPrice.toFixed(2)} with
                    </span>
                    <PlusMembershipChip plusMembershipShortBranding={plusMembershipShortBranding} />
                </p>
            )}
        </>
    );
}

NonMembershipPricing.propTypes = {
    plusMembershipShortBranding: PropTypes.string.isRequired,
    productData: PRODUCT_DATA_PROP_TYPE.isRequired,
    showMembershipPreviewPrice: PropTypes.bool.isRequired,
};

/**
 * Displays pricing information for members, including member price, original price if applicable,
 * and percentage discount.
 *
 * @param {Object} props Component props
 * @param {string} props.plusMembershipShortBranding Short branding text for plus membership (e.g. 'M+', 'IF+')
 * @param {import('./types').ProductData} props.productData Product data containing pricing information
 * @returns {JSX.Element} Pricing display for members
 */
function MembershipPricing({ plusMembershipShortBranding, productData }) {
    return (
        <>
            <div className="flex flex-wrap items-center gap-5">
                <span className="font-grotesk-bold text-body-mobile-sm text-cranberry md:text-body-mobile-md">
                    {productData.isBundle && <>{productData.portion} for </>}${productData.plusMemberPrice?.toFixed(2)}
                </span>{' '}
                <PlusMembershipChip plusMembershipShortBranding={plusMembershipShortBranding} />
            </div>
            {getShowOriginalPrice(productData, 0, true) && (
                <div className="flex gap-5 text-body-mobile-xs md:text-body-mobile-sm">
                    <span className="inline-block text-grey line-through">
                        ${getProductOriginalPrice(productData, true)?.toFixed(2)}
                    </span>
                    {!productData.isBundle && (
                        <span className={clsx({ 'text-error': productData.sale })}>
                            {getPercentOffOriginalPrice(productData, true)}%OFF
                        </span>
                    )}
                </div>
            )}
        </>
    );
}

MembershipPricing.propTypes = {
    plusMembershipShortBranding: PropTypes.string.isRequired,
    productData: PRODUCT_DATA_PROP_TYPE.isRequired,
};

/**
 * Renders children text in uppercase with specific styling for product card presentation.
 *
 * @param {Object} props Component props
 * @param {React.ReactNode} props.children Content to be displayed in uppercase
 * @returns {JSX.Element} Paragraph element with uppercase text styling
 */
function UpperCaseTag({ children }) {
    return <p className="text-body-xs mt-5 leading-[130%]! tracking-[0.03rem] text-black uppercase">{children}</p>;
}

export { NonMembershipPricing, MembershipPricing, ProductTag, ProductTags, ProductTitle, Wrapper, SourcingReason };
